import type { AntTreeNodeProps, DataNode } from "antd/es/tree";
import {
  ConsecutiveBreaker,
  circuitBreaker,
  handleAll,
  retry,
  wrap,
} from "cockatiel";
import type React from "react";
import { Spinner } from "react-bootstrap";
import { FileEarmark } from "react-bootstrap-icons";
import {
  type ExternalDocument,
  type ExternalFolder,
  ExternalPartners,
} from "@/briefpoint-client";
import { PopupNavigator } from "@/components/ExternalPartner/AuthPopup/PopupNavigator";
import { GetExternalPartnerAuthUrl } from "@/components/ExternalPartner/ExternalPartnerConnection";
import { useUserApi as UseUserApi } from "@/hooks/useApi";
import { useAuth as UseAuth } from "@/hooks/useAuth";
import UseConfig from "@/hooks/useConfig";
import FolderIcon from "@/images/folder-icon.svg?react";
import ClioLogo from "@/images/Glyph_Clio Glyph Blue.svg?react";
// IMAGES
import BriefpointLogo from "@/images/logo.svg?react";
import MyCaseLogo from "@/images/MyCase.svg?react";
import SmokeballLogo from "@/images/Smokeball_logo_mark.svg?react";
import SwitcherClosed from "@/images/tree-switcher-closed.svg?react";
import SwitchedOpened from "@/images/tree-switcher-opened.svg?react";
import type {
  ExtendedExternalFolder,
  ExternalClientIEnumerableExternalPartnerResponseResponse,
  ExternalDocumentResponse,
  UserResponse,
} from "@/lib/types";
import { readParams } from "@/lib/utils";

interface TreeNode extends DataNode {
  expanded?: boolean;
}

interface TreeNodeWithPath extends DataNode {
  path?: string;
}

export function delayDocumentRefresh(
  delay: number,
  loadDocuments: () => Promise<void>,
  setState: React.Dispatch<React.SetStateAction<boolean>>,
  finishAction: () => void,
): void {
  setTimeout(() => {
    loadDocuments();
    setState(false);
    finishAction();
  }, delay);
}

export function getCaseSourceLogo(partner?: ExternalPartners) {
  if (!partner || partner === ExternalPartners.None) {
    return <BriefpointLogo width="18px" title="Briefpoint Case" />;
  }

  switch (partner) {
    case ExternalPartners.Clio:
      return <ClioLogo title="Clio Case" />;
    case ExternalPartners.Smokeball:
      return <SmokeballLogo title="Smokeball Case" />;
    case ExternalPartners.MyCase:
      return <MyCaseLogo title="MyCase Case" />;
    default:
      return <BriefpointLogo title="Briefpoint Case" />;
  }
}

export const initiateAuth = async () => {
  const { user, setUser, firm } = UseAuth();
  const userApi = UseUserApi();
  const [config] = UseConfig();

  if (!user || !firm) {
    return null;
  }
  const userId = user.id;
  const firmProviders = firm.preferences?.enabledExternalProviders?.values();
  const enabledPartners = firmProviders ? Array.from(firmProviders) : null;

  if (!enabledPartners?.length) {
    return null;
  }

  const partner = enabledPartners[0];
  const authUrl = GetExternalPartnerAuthUrl(partner, userId, config);
  const popupNavigator = new PopupNavigator();
  const handle = await popupNavigator.prepare({});

  if (!authUrl) {
    // TODO: probably should throw an error here
    return null;
  }

  try {
    const result = await handle.navigate({
      url: authUrl,
    });

    const params = readParams(result.url);
    const code = params.get("code");
    if (code) {
      const tokenResult = await userApi.userExternalPartnerAuth({
        userId,
        code,
        partner: partner,
      });
      if (tokenResult.responseCode !== 200) {
        throw new Error(
          `Unable to authenticate to ${partner}. Status Code: ${tokenResult.responseCode}`,
        );
      }

      setUser((u) => {
        const newUser = { ...u } as UserResponse;
        newUser.externalConnection = { partner: partner, isActive: true };
        return newUser;
      });
    } else {
      // setSaving(false);
      throw new Error(`Unexpected response from auth: ${params}`);
    }
    // setSaving(false);
  } catch (_e) {
    let message = "";
    if (typeof _e === "string") {
      message = _e;
    } else if (_e instanceof Error) {
      message = _e.message;
    }

    if (message.includes("closed by user")) {
      // setFormError("Authentication cancelled");
      throw new Error("Authentication cancelled");
    }
    // setSaving(false);
  }
};

/**
 * Builds a map of folder IDs to their hasPreviouslySelected status
 * @param folders - The folder structure to process
 * @returns A Map where keys are folder IDs and values indicate if the folder has been previously viewed
 */
function buildFolderViewedMap(folders: ExternalFolder[]): Map<string, boolean> {
  const folderViewedMap = new Map<string, boolean>();
  const buildRecursive = (folderList: ExternalFolder[]): void => {
    for (const folder of folderList) {
      if (folder.id) {
        const extendedFolder = folder as ExtendedExternalFolder;
        folderViewedMap.set(
          folder.id,
          extendedFolder.hasPreviouslySelected === true,
        );
      }
      if (folder.folders && folder.folders.length > 0) {
        buildRecursive(folder.folders);
      }
    }
  };
  buildRecursive(folders);
  return folderViewedMap;
}

/**
 * Determines if a folder should show a loading spinner based on its state
 * @param isLoading - Whether data is currently loading
 * @param folderId - The folder ID to check
 * @param folderViewedMap - Map of folder IDs to their viewed status
 * @returns True if the folder should show loading spinner, false otherwise
 */
function shouldShowLoading(
  isLoading: boolean,
  folderId: string | undefined,
  folderViewedMap: Map<string, boolean>,
): boolean {
  if (!isLoading || !folderId) return false;
  const hasBeenViewed = folderViewedMap.get(folderId) === true;
  return !hasBeenViewed;
}

/**
 * Creates a placeholder node for empty folders (either "No items" or loading spinner)
 * @param folderKey - The key of the folder
 * @param shouldShowLoading - Whether to show loading spinner
 * @param path - Optional path for the placeholder node
 * @returns A tree node for the placeholder
 */
function createEmptyFolderPlaceholder(
  folderKey: React.Key,
  shouldShowLoading: boolean,
  path?: string,
): TreeNode {
  return {
    title: shouldShowLoading ? (
      <>
        <Spinner size="sm" className="me-2" />
        Loading...
      </>
    ) : (
      "No items"
    ),
    key: `${folderKey}-no-items`,
    disabled: true,
    selectable: false,
    icon: " ", // Leave it blank to prevent any icon from showing
    ...(path && { path }),
  } as TreeNode;
}

/**
 * Formats a document name, ensuring the type extension is included
 * @param name - The document name (can be null, undefined, or string)
 * @param type - The document type/extension (can be null, undefined, or string)
 * @returns The formatted document name with extension
 */
function formatDocumentName(
  name: string | null | undefined,
  type: string | null | undefined,
): string {
  const docName = name || "Unnamed Document";
  const docType = type || "";
  return docName.endsWith(docType) ? docName : `${docName}${docType}`;
}

/**
 * Marks the first folder in a tree structure with isFirstFolder flag
 * This allows the switcher icon to be hidden for the first folder
 * @param treeData - The tree data to process (can be TreeNode[] or TreeNodeWithPath[])
 * @returns True if a first folder was found and marked, false otherwise
 */
function markFirstFolderInTree(
  treeData: Array<TreeNode | TreeNodeWithPath>,
): boolean {
  const firstFolderKey = findFirstFolderKey(treeData);
  if (!firstFolderKey) return false;

  const markRecursive = (
    nodes: Array<TreeNode | TreeNodeWithPath>,
  ): boolean => {
    for (const node of nodes) {
      // Check if this is the first folder (not a leaf node)
      if (node.key === firstFolderKey && !node.isLeaf) {
        (
          node as (TreeNode | TreeNodeWithPath) & {
            isFirstFolder?: boolean;
          }
        ).isFirstFolder = true;
        return true;
      }
      if (node.children && node.children.length > 0) {
        if (
          markRecursive(node.children as Array<TreeNode | TreeNodeWithPath>)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  return markRecursive(treeData);
}

/**
 * Helper function to process root folder and determine if "Documents" folder should be created
 * @param rootFolder - The root folder (id: null, name: null)
 * @returns Object containing information about root folder processing
 */
function processRootFolder(rootFolder: ExternalFolder): {
  shouldCreateDocumentsFolder: boolean;
  rootDocuments: ExternalDocument[];
  subFolders: ExternalFolder[];
} {
  const rootDocuments =
    rootFolder.documents?.filter((doc) => !doc.folder) || [];
  const hasRootDocuments = rootDocuments.length > 0;
  const hasSubFolders = Boolean(
    rootFolder.folders && rootFolder.folders.length > 0,
  );
  const shouldCreateDocumentsFolder = hasRootDocuments || hasSubFolders;

  return {
    shouldCreateDocumentsFolder,
    rootDocuments,
    subFolders: rootFolder.folders || [],
  };
}

// Function to transform data into a tree structure (for importing from External Partner to BP or exporting to External Partner)
export const transformDataExternalToBp = (
  folders: ExternalFolder[],
  isLoading = false,
  options?: {
    includePath?: boolean; // If true, adds path property to nodes (for export mode)
    parentPath?: string; // Parent path for export mode
  },
) => {
  const includePath = options?.includePath ?? false;
  const parentPath = options?.parentPath ?? "";
  const tree: (TreeNode | TreeNodeWithPath)[] = [];
  const dataMap: Map<string, ExternalDocumentResponse> = new Map();
  const folderMap = new Map<
    string,
    { node: TreeNode | TreeNodeWithPath; path: string }
  >();
  const folderKeys = new Set<string>();

  // Build a map of folder IDs to their hasPreviouslySelected status
  const folderViewedMap = buildFolderViewedMap(folders);

  // Helper function to create a node with optional path
  const createNode = (
    title: string,
    key: React.Key,
    isLeaf: boolean,
    icon: React.ReactNode,
    path?: string,
    selectable?: boolean,
  ): TreeNode | TreeNodeWithPath => {
    const baseNode = {
      title,
      key,
      isLeaf,
      icon,
      children: isLeaf ? undefined : [],
    };

    if (includePath) {
      return {
        ...baseNode,
        path: path || key.toString(),
        selectable: selectable ?? !isLeaf,
      } as TreeNodeWithPath;
    }

    return baseNode as TreeNode;
  };

  // Helper function to recursively process folders
  const processFolder = (
    folder: ExternalFolder,
    currentParentPath = "",
    parentChildren: (TreeNode | TreeNodeWithPath)[] = tree,
  ): void => {
    if (!folder) return;

    // Skip root folder if it has no name (typically root folder has id: null, name: null)
    const isRootFolder = !folder.name && !folder.id;

    if (isRootFolder) {
      const rootFolderInfo = processRootFolder(folder);

      // Create a "Documents" folder if there are documents with folder: null or subfolders
      if (rootFolderInfo.shouldCreateDocumentsFolder) {
        const documentsFolderName = "Documents";
        const documentsFolderPath = includePath
          ? documentsFolderName
          : documentsFolderName;
        const documentsFolderKey = "root";

        const documentsFolderNode = createNode(
          documentsFolderName,
          documentsFolderKey,
          false,
          <FolderIcon />,
          documentsFolderPath,
          true,
        );

        // Track this as a folder key
        folderKeys.add(documentsFolderKey);

        // Add to parent children (tree root)
        parentChildren.push(documentsFolderNode);

        // Store in folder map for document matching (use "root" as the key)
        folderMap.set("root", {
          node: documentsFolderNode,
          path: documentsFolderPath,
        });

        // Process subfolders as children of the "Documents" folder, not directly to tree
        if (rootFolderInfo.subFolders.length > 0) {
          for (const subFolder of rootFolderInfo.subFolders) {
            processFolder(
              subFolder,
              documentsFolderPath,
              documentsFolderNode.children as (TreeNode | TreeNodeWithPath)[],
            );
          }
        }
      } else {
        // If no root documents and no subfolders, process normally (though this case is unlikely)
        if (rootFolderInfo.subFolders.length > 0) {
          for (const subFolder of rootFolderInfo.subFolders) {
            processFolder(subFolder, currentParentPath, parentChildren);
          }
        }
      }
      return;
    }

    // Create folder node for actual folders
    const folderName = folder.name || "Unnamed Folder";
    const folderPath = currentParentPath
      ? `${currentParentPath}/${folderName}`
      : folderName;

    const folderNode = createNode(
      folderName,
      folder.id || folderPath,
      false,
      <FolderIcon />,
      folderPath,
      includePath ? true : undefined,
    );

    // Track this as a folder key
    folderKeys.add(folder.id || folderPath);

    // Add to parent children
    parentChildren.push(folderNode);

    // Store in folder map for document matching
    if (folder.id) {
      folderMap.set(folder.id, { node: folderNode, path: folderPath });
    }

    // Process nested folders recursively
    if (folder.folders && folder.folders.length > 0) {
      for (const subFolder of folder.folders) {
        processFolder(
          subFolder,
          folderPath,
          folderNode.children as (TreeNode | TreeNodeWithPath)[],
        );
      }
    }
  };

  // Build folder structure first
  for (const folder of folders) {
    processFolder(folder, parentPath);
  }

  // Helper function to collect all documents
  const collectDocuments = (
    folderList: ExternalFolder[],
  ): ExternalDocument[] => {
    const docs: ExternalDocument[] = [];
    for (const folder of folderList) {
      if (folder.documents && folder.documents.length > 0) {
        docs.push(...folder.documents);
      }
      if (folder.folders && folder.folders.length > 0) {
        docs.push(...collectDocuments(folder.folders));
      }
    }
    return docs;
  };

  // Get all documents and add them to appropriate folders or root
  const allDocuments = collectDocuments(folders);

  // Check if we need to create a "Documents" folder for documents with folder: null
  const hasDocumentsWithoutFolder = allDocuments.some((doc) => !doc.folder);
  if (hasDocumentsWithoutFolder && !folderMap.has("root")) {
    // Create "Documents" folder if we have documents with folder: null but it wasn't created during folder processing
    const documentsFolderName = "Documents";
    const documentsFolderPath = documentsFolderName;
    const documentsFolderKey = "root";

    const documentsFolderNode = createNode(
      documentsFolderName,
      documentsFolderKey,
      false,
      <FolderIcon />,
      documentsFolderPath,
      includePath ? true : undefined,
    );

    // Move any existing folder nodes from tree root into "Documents" folder
    // (This handles edge cases where folders were added before "Documents" was created)
    const foldersToMove: (TreeNode | TreeNodeWithPath)[] = [];
    const filesToKeep: (TreeNode | TreeNodeWithPath)[] = [];
    for (const node of tree) {
      // Folders are non-leaf nodes (isLeaf is false or undefined and they have children or are marked as folders)
      if (!node.isLeaf && node.key !== documentsFolderKey) {
        foldersToMove.push(node);
      } else {
        filesToKeep.push(node);
      }
    }

    // Add moved folders to Documents folder's children
    documentsFolderNode.children = foldersToMove as DataNode[];

    folderKeys.add(documentsFolderKey);
    // Replace tree contents: Documents folder first, then any remaining files
    tree.length = 0;
    tree.push(documentsFolderNode);
    tree.push(...filesToKeep);
    folderMap.set("root", {
      node: documentsFolderNode,
      path: documentsFolderPath,
    });
  }

  for (const doc of allDocuments) {
    if (!doc.id) continue;

    // Store document in dataMap (cast to ExternalDocumentResponse for compatibility)
    dataMap.set(doc.id, doc as ExternalDocumentResponse);

    const docName = formatDocumentName(doc.name, doc.type);
    let docPath: string | undefined;
    if (includePath) {
      const folderInfo = folderMap.get(doc.folder || "root");
      if (folderInfo) {
        docPath = `${folderInfo.path}/${docName}`;
      } else {
        // Fallback: use parentPath if available
        docPath = parentPath ? `${parentPath}/${docName}` : docName;
      }
    }

    const docNode = createNode(
      docName,
      doc.id,
      true,
      <FileEarmark />,
      docPath,
      includePath ? false : undefined,
    );

    // Match document to folder based on folder field
    if (doc.folder && folderMap.has(doc.folder)) {
      const folderInfo = folderMap.get(doc.folder);
      if (folderInfo?.node.children) {
        folderInfo.node.children.push(docNode as DataNode);
      }
    } else if (!doc.folder && folderMap.has("root")) {
      // Document has no folder, add it to the "Documents" folder if it exists
      const rootFolderInfo = folderMap.get("root");
      if (rootFolderInfo?.node.children) {
        rootFolderInfo.node.children.push(docNode as DataNode);
      }
    } else {
      // Document belongs to root (fallback if no "Documents" folder was created)
      tree.push(docNode);
    }
  }

  // Helper function to recursively add "No items" or loading spinner to empty folders
  const addNoItemsToEmptyFolders = (
    nodes: (TreeNode | TreeNodeWithPath)[],
  ): void => {
    for (const node of nodes) {
      // Check if this is a folder (has children property and is not a leaf)
      if (node.children && !node.isLeaf) {
        if (node.children.length === 0) {
          const folderId = node.key?.toString();
          const showLoading = shouldShowLoading(
            isLoading,
            folderId,
            folderViewedMap,
          );
          const placeholder = createEmptyFolderPlaceholder(
            node.key,
            showLoading,
          );
          if (includePath && "path" in node) {
            const pathNode = placeholder as TreeNodeWithPath;
            pathNode.path = `${(node as TreeNodeWithPath).path}/no-items`;
            node.children.push(pathNode);
          } else {
            node.children.push(placeholder);
          }
        } else {
          // Recursively check nested folders
          addNoItemsToEmptyFolders(
            node.children as (TreeNode | TreeNodeWithPath)[],
          );
        }
      }
    }
  };

  // Add "No items" or loading spinner to any empty folders
  addNoItemsToEmptyFolders(tree);

  // Mark the first folder so we can hide its switcher icon
  markFirstFolderInTree(tree);

  // Return appropriate type based on mode
  if (includePath) {
    return tree as TreeNodeWithPath[];
  }
  return [tree as TreeNode[], dataMap, folderKeys] as [
    TreeNode[],
    Map<string, ExternalDocumentResponse>,
    Set<string>,
  ];
};

// Function to transform data into a tree structure (for exporting from BP Partner to External Partner)
// This is now a wrapper around transformDataExternalToBp with export options
export const transformDataBpToExternal = (
  data: ExternalFolder[],
  parentPath = "",
  isLoading = false,
): TreeNodeWithPath[] => {
  return transformDataExternalToBp(data, isLoading, {
    includePath: true,
    parentPath,
  }) as TreeNodeWithPath[];
};

export function TreeSwitcherIcon(props: AntTreeNodeProps): React.ReactNode {
  // Hide switcher icon for the first folder
  const nodeData = props.data as (TreeNode | TreeNodeWithPath) & {
    isFirstFolder?: boolean;
  };
  if (nodeData?.isFirstFolder) {
    return null;
  }
  return props.expanded ? <SwitchedOpened /> : <SwitcherClosed />;
}

export async function externalRequestWrapper(fetchData: unknown, attempts = 1) {
  // Attempt to retry if no response/timeout/network error occurs
  const retryPolicy = retry(handleAll, { maxAttempts: attempts });

  // Stops calling the executed function for 10 seconds if it fails 5 times in a row.
  const circuitBreakerPolicy = circuitBreaker(handleAll, {
    halfOpenAfter: 10 * 1000,
    breaker: new ConsecutiveBreaker(5),
  });

  const retryWithBreaker = wrap(retryPolicy, circuitBreakerPolicy);

  const result = await retryWithBreaker.execute(() => {
    return fetchData;
  });

  return result;
}

export async function handleExternalReqResCodes(
  res: ExternalClientIEnumerableExternalPartnerResponseResponse,
  onSuccess: (
    res: ExternalClientIEnumerableExternalPartnerResponseResponse,
  ) => void,
  onError: (
    res: ExternalClientIEnumerableExternalPartnerResponseResponse,
  ) => void,
  initiateAuth: () => Promise<null | undefined>,
) {
  switch (res.responseCode) {
    case 200:
    case 201:
    case 202:
      onSuccess(res);
      break;
    case 401:
      initiateAuth();
      break;
    default:
      onError(res); // Now passes the response to the error callback
  }
}

/**
 * Recursively finds and merges new folder data into existing folder structure
 * @param existingFolders - The current folder structure
 * @param newFolders - The new folders/documents to merge
 * @param targetFolderId - The ID of the folder to merge into (if undefined, returns newFolders)
 * @returns The merged folder array
 */
export function mergeFolderData(
  existingFolders: Array<ExternalFolder>,
  newFolders: Array<ExternalFolder>,
  targetFolderId: string | undefined,
): Array<ExternalFolder> {
  if (!targetFolderId) {
    // Initial load: return new folders
    return newFolders;
  }

  // Find the target folder in the existing structure and merge the new data
  const findAndMerge = (
    folders: Array<ExternalFolder>,
  ): Array<ExternalFolder> => {
    return folders.map((folder) => {
      if (folder.id === targetFolderId) {
        // Found the target folder, merge the new folders and documents
        // Get existing IDs to avoid duplicates
        const existingFolderIds = new Set(
          (folder.folders || []).map((f) => f.id).filter(Boolean),
        );
        const existingDocumentIds = new Set(
          (folder.documents || []).map((d) => d.id).filter(Boolean),
        );

        // Filter out duplicates from new folders
        const newFoldersToAdd =
          newFolders[0]?.folders
            ?.filter((f) => f.id && !existingFolderIds.has(f.id))
            .map((f) => ({ ...f, hasPreviouslySelected: false })) || [];

        // Filter out duplicates from new documents
        const newDocumentsToAdd =
          newFolders[0]?.documents?.filter(
            (d) => d.id && !existingDocumentIds.has(d.id),
          ) || [];

        return {
          ...folder,
          folders: [...(folder.folders || []), ...newFoldersToAdd],
          documents: [...(folder.documents || []), ...newDocumentsToAdd],
        };
      }
      // Recursively search in nested folders
      if (folder.folders && folder.folders.length > 0) {
        return {
          ...folder,
          folders: findAndMerge(folder.folders),
        };
      }
      return folder;
    });
  };

  return findAndMerge(existingFolders);
}

/**
 * Checks if a folder already has children (folders or documents) loaded
 * @param folders - The folder structure to search through
 * @param folderId - The ID of the folder to check
 * @returns True if the folder has children loaded, false otherwise
 */
export function hasFolderChildrenLoaded(
  folders: Array<ExternalFolder>,
  folderId: string | undefined,
): boolean {
  if (!folderId || !folders || folders.length === 0) {
    return false;
  }

  const findFolder = (folderList: Array<ExternalFolder>): boolean => {
    for (const folder of folderList) {
      if (folder.id === folderId) {
        // Check if this folder has any folders or documents loaded
        return (
          Boolean(folder.folders && folder.folders.length > 0) ||
          Boolean(folder.documents && folder.documents.length > 0)
        );
      }
      // Recursively search in nested folders
      if (folder.folders && folder.folders.length > 0) {
        if (findFolder(folder.folders)) {
          return true;
        }
      }
    }
    return false;
  };

  return findFolder(folders);
}

/**
 * Recursively updates a folder's flag to mark it as previously selected
 * @param folders - The folder structure to update
 * @param folderId - The ID of the folder to update
 * @returns The updated folder structure with the flag set
 */
export function updateFolderFlag<T extends ExternalFolder>(
  folders: Array<T>,
  folderId: string | undefined,
): Array<T> {
  if (!folderId) return folders;

  return folders.map((folder) => {
    if (folder.id === folderId) {
      return { ...folder, hasPreviouslySelected: true } as T;
    }
    if (folder.folders && folder.folders.length > 0) {
      return {
        ...folder,
        folders: updateFolderFlag(folder.folders, folderId),
      } as T;
    }
    return folder;
  });
}

/**
 * Recursively finds a folder by ID in a folder structure
 * @param folders - The folder structure to search
 * @param folderId - The ID of the folder to find
 * @returns The folder if found, undefined otherwise
 */
export function findFolderRecursive<T extends ExternalFolder>(
  folders: Array<T>,
  folderId: string | undefined,
): T | undefined {
  if (!folderId || !folders || folders.length === 0) {
    return undefined;
  }

  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }
    if (folder.folders && folder.folders.length > 0) {
      const found = findFolderRecursive(folder.folders, folderId);
      if (found) return found as T;
    }
  }
  return undefined;
}

/**
 * Extracts folder keys from a tree data structure
 * @param treeData - The tree data to extract keys from
 * @returns A Set of folder keys (strings)
 */
export function extractFolderKeysFromTree(
  treeData: Array<{
    key?: React.Key;
    selectable?: boolean;
    isLeaf?: boolean;
    children?: unknown[];
  }>,
): Set<string> {
  const keys = new Set<string>();
  const extractRecursive = (
    nodes: Array<{
      key?: React.Key;
      selectable?: boolean;
      isLeaf?: boolean;
      children?: unknown[];
    }>,
  ): void => {
    nodes.forEach((node) => {
      // Folders have selectable: true and are not leaves
      if (node.selectable && !node.isLeaf && node.key) {
        keys.add(node.key.toString());
        if (node.children && node.children.length > 0) {
          extractRecursive(
            node.children as Array<{
              key?: React.Key;
              selectable?: boolean;
              isLeaf?: boolean;
              children?: unknown[];
            }>,
          );
        }
      }
    });
  };
  extractRecursive(treeData);
  return keys;
}

/**
 * Finds the first folder key in a tree data structure
 * @param treeData - The tree data to search
 * @returns The first folder key found, or undefined if no folder exists
 */
export function findFirstFolderKey(
  treeData: Array<{
    key?: React.Key;
    selectable?: boolean;
    isLeaf?: boolean;
    children?: unknown[];
  }>,
): React.Key | undefined {
  const findRecursive = (
    nodes: Array<{
      key?: React.Key;
      selectable?: boolean;
      isLeaf?: boolean;
      children?: unknown[];
    }>,
  ): React.Key | undefined => {
    for (const node of nodes) {
      // Folders are nodes that are not leaves (isLeaf is false or undefined) and have a key
      // They may have selectable: true (in transformDataBpToExternal) or not (in transformDataExternalToBp)
      // But they always have isLeaf: false or undefined
      const isFolder =
        node.key &&
        (node.isLeaf === false ||
          (!node.isLeaf && node.children !== undefined));

      if (isFolder) {
        return node.key;
      }

      // Recursively search children if present
      if (node.children && node.children.length > 0) {
        const found = findRecursive(
          node.children as Array<{
            key?: React.Key;
            selectable?: boolean;
            isLeaf?: boolean;
            children?: unknown[];
          }>,
        );
        if (found) return found;
      }
    }
    return undefined;
  };
  return findRecursive(treeData);
}

/**
 * Handles folder selection logic including expansion and loading children
 * @param selectedKeys - The keys that were selected
 * @param folderKeys - Set of folder keys
 * @param mappedExternalFolders - The current folder structure
 * @param expandedKeys - Current expanded keys
 * @param setExpandedKeys - Function to update expanded keys
 * @param setSelectedFolderId - Function to set the selected folder ID
 * @returns The selected folder key if one was selected, undefined otherwise
 */
export function handleFolderSelection(
  selectedKeys: React.Key[],
  folderKeys: Set<string>,
  mappedExternalFolders: Array<ExtendedExternalFolder> | undefined,
  expandedKeys: React.Key[],
  setExpandedKeys: (keys: React.Key[]) => void,
  setSelectedFolderId: (id: string | undefined) => void,
): string | undefined {
  // Check if any selected key is a folder
  const hasFolderSelected = selectedKeys.some((key) =>
    folderKeys.has(key.toString()),
  );

  if (!hasFolderSelected) {
    return undefined;
  }

  const selectedFolderKey = selectedKeys
    .find((key) => folderKeys.has(key.toString()))
    ?.toString();

  if (!selectedFolderKey) {
    return undefined;
  }

  // Automatically expand the selected folder
  if (!expandedKeys.includes(selectedFolderKey)) {
    setExpandedKeys([...expandedKeys, selectedFolderKey]);
  }

  const hasBeenViewed = findFolderRecursive(
    mappedExternalFolders || [],
    selectedFolderKey,
  )?.hasPreviouslySelected;

  // Check if the folder already has children loaded to prevent unnecessary API calls
  const alreadyHasChildren = hasFolderChildrenLoaded(
    mappedExternalFolders || [],
    selectedFolderKey,
  );

  if (!alreadyHasChildren && !hasBeenViewed) {
    setSelectedFolderId(selectedFolderKey);
  }

  return selectedFolderKey;
}

/**
 * Adds a new folder to the folder structure immutably
 * @param folders - The current folder structure
 * @param newFolder - The new folder to add
 * @param parentFolderId - The ID of the parent folder to add the new folder to
 * @returns The updated folder structure with the new folder added
 */
export function addFolderToStructure<T extends ExtendedExternalFolder>(
  folders: Array<T>,
  newFolder: T,
  parentFolderId: string | undefined,
): Array<T> {
  if (!parentFolderId) {
    // If no parent, add to root level
    return [...folders, newFolder];
  }

  const updateFolders = (folderList: Array<T>): Array<T> => {
    return folderList.map((folder) => {
      if (folder.id === parentFolderId) {
        // Found the parent folder, add the new folder
        return {
          ...folder,
          folders: [...(folder.folders || []), newFolder],
        } as T;
      }
      // Recursively search in nested folders
      if (folder.folders && folder.folders.length > 0) {
        return {
          ...folder,
          folders: updateFolders(folder.folders as Array<T>),
        } as T;
      }
      return folder;
    });
  };

  return updateFolders(folders);
}
